import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QrCode, Info, MapPin } from 'lucide-react';
import { useZxing } from 'react-zxing';
import { clubsService, eventsService, scoringService } from '../../../../../../services';
import { compareStandsByIdentity } from '../../../../../../pages/dashboard/ManageClubs/standClayConfig';
import './FunShootQuickLaunch.css';

const DEFAULT_TARGET_LABELS = ['A', 'B', 'C', 'D', 'E', 'F'];
const clayDisciplineTypes = {
    Skeet: ['EnglishSkeet', 'AmericanSkeet', 'OlympicSkeet', 'OlympicSkeetPre2012', 'OlympicSkeetFinal', 'SkeetDoubles', 'SkeetShootOff', 'WobbleSkeet'],
    Sporting: ['CompakSporting', 'EnglishSporting', 'FITASCSporting', 'FiveStand', 'Sportrap', 'SuperSporting'],
    Trap: ['AmericanTrap', 'AmericanTrapDoubles', 'DoubleBarrel', 'DoubleRise', 'DownTheLine', 'OlympicDoubleTrap', 'OlympicTrap', 'SingleBarrel', 'WobbleTrap']
};

const summariseClayConfig = (clayConfigJson) => {
    if (!clayConfigJson) {
        return { standLabel: '', targetLabels: [] };
    }

    try {
        const parsed = JSON.parse(clayConfigJson);
        const stands = Array.isArray(parsed?.stands) ? parsed.stands : Array.isArray(parsed) ? parsed : [];
        const firstStand = stands[0] || parsed || {};
        const targetLabels = Array.isArray(firstStand?.targetLabels)
            ? firstStand.targetLabels
            : Array.isArray(firstStand?.targets)
                ? firstStand.targets.map((target, index) =>
                    typeof target === 'string' ? target : target?.label || target?.name || `T${index + 1}`
                )
                : Array.isArray(firstStand?.clays)
                    ? firstStand.clays.map((target, index) =>
                        typeof target === 'string' ? target : target?.label || target?.name || `T${index + 1}`
                    )
                    : [];

        return {
            standLabel: firstStand?.standName || firstStand?.name || firstStand?.label || '',
            targetLabels: targetLabels.filter(Boolean)
        };
    } catch {
        return { standLabel: '', targetLabels: [] };
    }
};

const buildAdhocClayConfig = (standName, targetCount, standCount) => {
    const safeStandCount = Math.max(1, Number(standCount) || 1);
    const targetLabels = DEFAULT_TARGET_LABELS.slice(0, Math.max(1, Math.min(targetCount, DEFAULT_TARGET_LABELS.length)));
    const stands = Array.from({ length: safeStandCount }, (_, index) => ({
        standNumber: index + 1,
        standName: index === 0 ? (standName || '') : '',
        targetLabels
    }));

    return JSON.stringify({
        totalStands: safeStandCount,
        stands
    });
};

const getShooterDisplayName = (shooter) =>
    shooter?.shooterName || `${shooter?.firstName || ''} ${shooter?.lastName || ''}`.trim() || shooter?.userName || 'Shooter';

const getShooterInitials = (label) => {
    const parts = String(label || 'SC').split(' ').filter(Boolean).slice(0, 2);
    return parts.map((part) => part[0]?.toUpperCase()).join('') || 'SC';
};

const isFunShootSheet = (sheet) =>
    String(sheet?.scoreSheetType || '').toLowerCase() === 'funshoot' ||
    String(sheet?.variant || '').toLowerCase() === 'funshoot';

const formatRelativeTimestamp = (value) => {
    if (!value) {
        return 'Recent';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return 'Recent';
    }

    return parsed.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short'
    });
};

const determineDisciplineAndVariant = (stand) => {
    if (!stand) {
        return { discipline: null, variant: null };
    }

    if (stand.discipline && clayDisciplineTypes[stand.discipline]) {
        let standVariant = stand.variant;

        if (standVariant && standVariant.includes(' ')) {
            standVariant = standVariant.split(' ')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join('');
        }

        if (standVariant && clayDisciplineTypes[stand.discipline].includes(standVariant)) {
            return { discipline: stand.discipline, variant: standVariant };
        }

        if (standVariant) {
            const formattedVariant = standVariant.toLowerCase().replace(/\s+/g, '');
            const matchedVariant = clayDisciplineTypes[stand.discipline].find((variantName) =>
                variantName.toLowerCase().replace(/\s+/g, '') === formattedVariant
            );

            if (matchedVariant) {
                return { discipline: stand.discipline, variant: matchedVariant };
            }
        }

        return { discipline: stand.discipline, variant: null };
    }

    const standTypeLower = (stand.standType || '').toLowerCase();

    for (const [discipline, variants] of Object.entries(clayDisciplineTypes)) {
        for (const variantName of variants) {
            const variantLower = variantName.toLowerCase();
            const variantSpaced = variantName.replace(/([A-Z])/g, ' $1').trim().toLowerCase();

            if (standTypeLower.includes(variantLower) || standTypeLower.includes(variantSpaced)) {
                return { discipline, variant: variantName };
            }
        }
    }

    if (standTypeLower.includes('skeet')) {
        return { discipline: 'Skeet', variant: 'EnglishSkeet' };
    }

    if (standTypeLower.includes('trap')) {
        return { discipline: 'Trap', variant: 'DownTheLine' };
    }

    if (standTypeLower.includes('sporting') || standTypeLower.includes('sport')) {
        return { discipline: 'Sporting', variant: 'EnglishSporting' };
    }

    return { discipline: null, variant: null };
};

const FunShootQuickLaunch = ({ event, isOpen, onClose, initialStandId = null }) => {
    const navigate = useNavigate();
    const [stands, setStands] = useState([]);
    const [availableShooters, setAvailableShooters] = useState([]);
    const [selectedShooters, setSelectedShooters] = useState([]);
    const [selectedStand, setSelectedStand] = useState(null);
    const [standName, setStandName] = useState('');
    const [adhocTargetCount, setAdhocTargetCount] = useState(2);
    const [adhocStandCount, setAdhocStandCount] = useState(5);
    const [manualStandCode, setManualStandCode] = useState('');
    const [showStandInfo, setShowStandInfo] = useState(false);
    const [launchMode, setLaunchMode] = useState('saved');
    const [showQrScanner, setShowQrScanner] = useState(false);
    const [scanError, setScanError] = useState(null);
    const [recentFunShoots, setRecentFunShoots] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const fetchLaunchData = useCallback(async () => {
        if (!event?.eventId) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const eventClubId = event.clubId || event.ClubId;
            const [shooters, eventDetails] = await Promise.all([
                eventsService.getEventShooters(event.eventId),
                eventClubId ? Promise.resolve(null) : eventsService.getEvent(event.eventId)
            ]);

            const resolvedClubId = eventClubId || eventDetails?.clubId || eventDetails?.ClubId;
            const clubStands = resolvedClubId
                ? await clubsService.getPublicClubStands(resolvedClubId)
                : [];
            const scoreSheets = await scoringService.getEventScoreSheets(event.eventId);

            setAvailableShooters(Array.isArray(shooters) ? shooters : []);
            setStands((Array.isArray(clubStands) ? clubStands : []).sort(compareStandsByIdentity));
            setRecentFunShoots(
                (Array.isArray(scoreSheets) ? scoreSheets : [])
                    .filter(isFunShootSheet)
                    .sort((left, right) => new Date(right.createdAt || right.updatedAt || 0) - new Date(left.createdAt || left.updatedAt || 0))
                    .slice(0, 5)
            );
        } catch (fetchError) {
            console.error('Error loading fun shoot launch data:', fetchError);
            setError('Failed to load event shooters or stands.');
        } finally {
            setIsLoading(false);
        }
    }, [event]);

    useEffect(() => {
        if (!isOpen || !event) {
            return;
        }

        setSelectedShooters([]);
        setSelectedStand(null);
        setStandName('');
        setAdhocTargetCount(2);
        setAdhocStandCount(5);
        setManualStandCode('');
        setShowStandInfo(false);
        setShowQrScanner(false);
        setScanError(null);
        setRecentFunShoots([]);
        setLaunchMode((event.clubId || event.ClubId) ? 'saved' : 'adhoc');
        fetchLaunchData();
    }, [isOpen, event, fetchLaunchData]);

    useEffect(() => {
        if (!isOpen || !initialStandId || !stands.length) {
            return;
        }

        const matchingStand = stands.find((stand) => String(stand.standId) === String(initialStandId));
        if (!matchingStand) {
            return;
        }

        setSelectedStand(matchingStand);
        setStandName(matchingStand.name || '');
        setManualStandCode(matchingStand.standId?.toString() || '');
        setLaunchMode('saved');
    }, [initialStandId, isOpen, stands]);

    useEffect(() => {
        if (!isOpen || initialStandId || stands.length !== 1 || selectedStand) {
            return;
        }

        const [stand] = stands;
        setSelectedStand(stand);
        setStandName(stand.name || '');
        setManualStandCode(stand.standId?.toString() || '');
        setLaunchMode('saved');
    }, [initialStandId, isOpen, selectedStand, stands]);

    const requestCameraAccess = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            stream.getTracks().forEach((track) => track.stop());
            return true;
        } catch (cameraError) {
            console.error('Camera access denied or unavailable:', cameraError);
            setScanError('Unable to access camera. Please check permissions.');
            return false;
        }
    };

    const handleQrScan = useCallback((data) => {
        if (!data) {
            return;
        }

        try {
            let standLookup = '';
            const parts = data.includes('|') ? data.split('|') : [data];
            [standLookup] = parts;

            if (standLookup && standLookup.startsWith('http')) {
                const idMatch = standLookup.match(/\/stand\/(\d+)/);
                if (idMatch?.[1]) {
                    standLookup = idMatch[1];
                }
            }

            const normalizedLookup = standLookup.trim().toLowerCase();
            const stand = stands.find((candidateStand) =>
                candidateStand.standId.toString() === standLookup.toString() ||
                (candidateStand.name || '').trim().toLowerCase() === normalizedLookup
            );
            if (!stand) {
                setScanError('Stand not found for this club.');
                return;
            }

            setSelectedStand(stand);
            setStandName(stand.name || '');
            setManualStandCode(stand.standId?.toString() || '');
            setLaunchMode('scan');
            setShowQrScanner(false);
            setScanError(null);
        } catch (scanFailure) {
            console.error('QR Scan Error:', scanFailure);
            setScanError('Invalid QR code format.');
        }
    }, [stands]);

    const { ref } = useZxing({
        onDecodeResult(result) {
            handleQrScan(result.getText());
        },
        onDecodeError(scanDecodeError) {
            if (scanDecodeError.message !== 'No MultiFormat Readers were able to detect the code.') {
                console.error('QR scan error:', scanDecodeError);
            }
        },
        constraints: { video: { facingMode: { ideal: 'environment' } } },
        timeBetweenDecodingAttempts: 300
    });

    const openQrScanner = async () => {
        const granted = await requestCameraAccess();
        if (granted) {
            setScanError(null);
            setShowQrScanner(true);
        }
    };

    const toggleShooter = (shooter) => {
        setSelectedShooters((current) =>
            current.some((selected) => selected.eventShooterId === shooter.eventShooterId)
                ? current.filter((selected) => selected.eventShooterId !== shooter.eventShooterId)
                : [...current, shooter]
        );
    };

    const handleStandChange = (eventValue) => {
        const stand = stands.find((candidateStand) => candidateStand.standId.toString() === eventValue.target.value);
        if (!stand) {
            return;
        }

        setSelectedStand(stand);
        setStandName(stand.name || '');
        setManualStandCode(stand.standId?.toString() || '');
        setLaunchMode('saved');
    };

    const handleManualStandLookup = () => {
        if (!manualStandCode.trim()) {
            setScanError('Enter a stand code or stand name.');
            return;
        }

        handleQrScan(manualStandCode.trim());
    };

    const selectedStandSummary = useMemo(
        () => summariseClayConfig(selectedStand?.clayConfigJson),
        [selectedStand]
    );
    const selectedStandSetup = useMemo(
        () => determineDisciplineAndVariant(selectedStand),
        [selectedStand]
    );

    const handleLaunch = async () => {
        if (!event?.eventId) {
            setError('No event selected.');
            return;
        }

        if (selectedShooters.length === 0) {
            setError('Pick at least one shooter.');
            return;
        }

        if (launchMode === 'adhoc' && !standName.trim()) {
            setError('Add a stand name or note for this ad hoc stand.');
            return;
        }

        if ((launchMode === 'saved' || launchMode === 'scan') && !selectedStand) {
            setError('Pick or scan a stand first.');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const effectiveDisciplineType = launchMode === 'adhoc'
                ? 'Sporting'
                : selectedStandSetup.discipline || 'Sporting';

            const data = await scoringService.createScoreSheet({
                eventId: event.eventId,
                disciplineType: effectiveDisciplineType,
                variant: 'FunShoot',
                scoreSheetType: 'FunShoot',
                standName: standName.trim(),
                clayConfigJson: launchMode === 'adhoc'
                    ? buildAdhocClayConfig(standName.trim(), adhocTargetCount, adhocStandCount)
                    : selectedStand?.clayConfigJson || null,
                shooterIds: selectedShooters.map((shooter) => shooter.eventShooterId)
            });

            onClose();
            navigate(`/dashboard/score/fun-shoot/${data.scoreSheetId}`);
        } catch (launchError) {
            console.error('Error starting fun shoot:', launchError);
            setError('Could not start this fun shoot.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const openExistingFunShoot = (scoreSheetId) => {
        onClose();
        navigate(`/dashboard/score/fun-shoot/${scoreSheetId}`);
    };

    const quickModeOptions = useMemo(() => {
        const options = [];
        if (stands.length > 0) {
            options.push({ id: 'saved', label: 'Saved Stand' });
            options.push({ id: 'scan', label: 'Scan QR' });
        }
        options.push({ id: 'adhoc', label: 'Ad Hoc Stand' });
        return options;
    }, [stands.length]);

    if (!isOpen || !event) {
        return null;
    }

    return (
        <div className="funshoot-launch-modal active">
            <div className="funshoot-launch-overlay" onClick={onClose}></div>
            <div className="funshoot-launch-box">
                <div className="funshoot-launch-header">
                    <div>
                        <h2>Quick Fun Shoot</h2>
                        <p>{event.name}</p>
                    </div>
                    <button type="button" className="funshoot-launch-close" onClick={onClose}>x</button>
                </div>

                {error && <div className="scoresheet-error-message">{error}</div>}

                <div className="funshoot-launch-card">
                    <h5>1. How are you starting?</h5>
                    <div className="funshoot-launch-modes">
                        {quickModeOptions.map((option) => (
                            <button
                                key={option.id}
                                type="button"
                                className={`funshoot-launch-mode ${launchMode === option.id ? 'active' : ''}`}
                                onClick={() => {
                                    setLaunchMode(option.id);
                                    if (option.id === 'scan') {
                                        openQrScanner();
                                    }
                                    if (option.id === 'adhoc') {
                                        setSelectedStand(null);
                                        setShowStandInfo(false);
                                        setStandName('');
                                        setAdhocTargetCount(2);
                                        setAdhocStandCount(5);
                                    }
                                }}
                                disabled={isLoading || isSubmitting}
                            >
                                {option.id === 'scan' && <QrCode size={16} />}
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>

                {(launchMode === 'saved' || launchMode === 'scan') && stands.length > 0 && (
                    <div className="funshoot-launch-card">
                        <h5>2. Pick the stand</h5>
                        <div className="funshoot-launch-stand-row">
                            <select
                                value={selectedStand ? selectedStand.standId : ''}
                                onChange={handleStandChange}
                                disabled={isLoading || isSubmitting}
                            >
                                <option value="">-- Select a Stand --</option>
                                {stands.map((stand) => (
                                    <option key={stand.standId} value={stand.standId}>
                                        {stand.name} ({stand.standType || stand.discipline || 'No type'})
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className="funshoot-launch-secondary"
                                onClick={openQrScanner}
                                disabled={isLoading || isSubmitting}
                            >
                                <QrCode size={16} />
                                Scan
                            </button>
                            {selectedStand && (
                                <button
                                    type="button"
                                    className="funshoot-launch-secondary"
                                    onClick={() => setShowStandInfo((current) => !current)}
                                >
                                    <Info size={16} />
                                    {showStandInfo ? 'Hide' : 'Info'}
                                </button>
                            )}
                        </div>
                        {selectedStand && (
                            <div className="funshoot-launch-stand-badge">
                                <MapPin size={14} />
                                <span>{standName || selectedStand.name}</span>
                            </div>
                        )}
                        {showStandInfo && selectedStand && (
                            <div className="funshoot-launch-stand-info">
                                <h6>{selectedStand.name}</h6>
                                {(selectedStandSetup.discipline || selectedStandSetup.variant || selectedStand.variant) && (
                                    <div className="funshoot-launch-stand-meta">
                                        {selectedStandSetup.discipline ? <span>{selectedStandSetup.discipline}</span> : null}
                                        {(selectedStandSetup.variant || selectedStand.variant) ? <span>{selectedStandSetup.variant || selectedStand.variant}</span> : null}
                                    </div>
                                )}
                                {selectedStand.description && <p>{selectedStand.description}</p>}
                            </div>
                        )}
                    </div>
                )}

                {launchMode === 'adhoc' && (
                    <div className="funshoot-launch-card">
                        <h5>2. Build the ad hoc stand</h5>
                        <input
                            type="text"
                            value={standName}
                            onChange={(eventValue) => setStandName(eventValue.target.value)}
                            placeholder="Tower pair, rabbit bank, crossing left-right..."
                            disabled={isLoading || isSubmitting}
                        />
                        <div className="funshoot-launch-preview-inline">
                            <span className="funshoot-launch-preview-label">Available clay labels</span>
                            <div className="funshoot-launch-segmented">
                                {DEFAULT_TARGET_LABELS.map((label, index) => {
                                    const count = index + 1;
                                    return (
                                        <button
                                            key={label}
                                            type="button"
                                            className={`funshoot-launch-segment ${adhocTargetCount === count ? 'active' : ''}`}
                                            onClick={() => setAdhocTargetCount(count)}
                                            disabled={isLoading || isSubmitting}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="funshoot-launch-helper">
                                Pick the highest clay label available on this stand. You can still score mixed presentations in any order once you start.
                            </p>
                        </div>
                        <div className="funshoot-launch-preview-inline">
                            <span className="funshoot-launch-preview-label">Planned stand count</span>
                            <input
                                type="number"
                                min="1"
                                max="50"
                                value={adhocStandCount}
                                onChange={(eventValue) => setAdhocStandCount(Math.max(1, Number(eventValue.target.value) || 1))}
                                disabled={isLoading || isSubmitting}
                            />
                            <p className="funshoot-launch-helper">
                                This controls how many stands are shown initially. You can still add more while scoring.
                            </p>
                        </div>
                    </div>
                )}

                <div className="funshoot-launch-card">
                    <h5>3. Pick shooters</h5>
                    {isLoading ? (
                        <div className="scoresheet-loading">Loading shooters...</div>
                    ) : (
                        <>
                            {availableShooters.length === 0 && (
                                <div className="scoresheet-error-message">
                                    Add at least one shooter to this event before starting a fun shoot.
                                </div>
                            )}
                            <div className="funshoot-launch-inline-actions">
                                <button
                                    type="button"
                                    className="funshoot-launch-secondary"
                                    onClick={() => setSelectedShooters(availableShooters)}
                                    disabled={isSubmitting || availableShooters.length === 0}
                                >
                                    Select All
                                </button>
                                <button
                                    type="button"
                                    className="funshoot-launch-secondary"
                                    onClick={() => setSelectedShooters([])}
                                    disabled={isSubmitting || selectedShooters.length === 0}
                                >
                                    Clear
                                </button>
                            </div>
                            <div className="funshoot-launch-shooters">
                                {availableShooters.map((shooter) => {
                                    const label = getShooterDisplayName(shooter);
                                    const checked = selectedShooters.some((selected) => selected.eventShooterId === shooter.eventShooterId);

                                    return (
                                        <label key={shooter.eventShooterId} className={`funshoot-launch-shooter ${checked ? 'selected' : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleShooter(shooter)}
                                                disabled={isSubmitting}
                                            />
                                            {shooter.profilePictureUrl ? (
                                                <img
                                                    src={shooter.profilePictureUrl}
                                                    alt={label}
                                                    className="funshoot-launch-shooter-avatar funshoot-launch-shooter-avatar-image"
                                                />
                                            ) : (
                                                <span className="funshoot-launch-shooter-avatar">
                                                    {getShooterInitials(label)}
                                                </span>
                                            )}
                                            <span className="funshoot-launch-shooter-name">{label}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                {recentFunShoots.length > 0 && (
                    <div className="funshoot-launch-card">
                        <h5>Recent fun shoots for this event</h5>
                        <p className="funshoot-launch-helper">
                            Resume a recent session here instead of going back through score shoot.
                        </p>
                        <div className="funshoot-launch-history">
                            {recentFunShoots.map((sheet) => (
                                <button
                                    key={sheet.scoreSheetId}
                                    type="button"
                                    className="funshoot-launch-history-card"
                                    onClick={() => openExistingFunShoot(sheet.scoreSheetId)}
                                >
                                    <div>
                                        <strong>{sheet.standName || sheet.name || 'Fun Shoot Session'}</strong>
                                        <p>
                                            {sheet.disciplineType || 'Clay'} · {formatRelativeTimestamp(sheet.createdAt || sheet.updatedAt)}
                                        </p>
                                    </div>
                                    <span>Open</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {launchMode === 'scan' && stands.length > 0 && (
                    <div className="funshoot-launch-card">
                        <h5>Scan fallback</h5>
                        <p className="funshoot-launch-helper">If the camera is awkward in the field, paste or type the stand code instead.</p>
                        <div className="funshoot-launch-stand-row">
                            <input
                                type="text"
                                value={manualStandCode}
                                onChange={(eventValue) => setManualStandCode(eventValue.target.value)}
                                placeholder="Stand QR text, stand ID, or stand name"
                                disabled={isLoading || isSubmitting}
                            />
                            <button
                                type="button"
                                className="funshoot-launch-secondary"
                                onClick={handleManualStandLookup}
                                disabled={isLoading || isSubmitting}
                            >
                                Find Stand
                            </button>
                        </div>
                        {scanError && <div className="scoresheet-error-message">{scanError}</div>}
                    </div>
                )}

                {selectedStand && (
                    <div className="funshoot-launch-card">
                        <h5>Stand preview</h5>
                        <div className="funshoot-launch-preview-grid">
                            <div>
                                <strong>{selectedStand.name}</strong>
                                <p>{selectedStandSummary.standLabel || selectedStand.description || 'Saved stand ready to launch.'}</p>
                                {(selectedStandSetup.discipline || selectedStandSetup.variant || selectedStand.variant) && (
                                    <div className="funshoot-launch-preview-meta">
                                        {selectedStandSetup.discipline ? (
                                            <span className="funshoot-launch-preview-tag funshoot-launch-preview-tag-secondary">
                                                {selectedStandSetup.discipline}
                                            </span>
                                        ) : null}
                                        {(selectedStandSetup.variant || selectedStand.variant) ? (
                                            <span className="funshoot-launch-preview-tag funshoot-launch-preview-tag-secondary">
                                                {selectedStandSetup.variant || selectedStand.variant}
                                            </span>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                            <div>
                                <span className="funshoot-launch-preview-label">Targets</span>
                                <div className="funshoot-launch-preview-tags">
                                    {selectedStandSummary.targetLabels.length > 0 ? selectedStandSummary.targetLabels.map((label) => (
                                        <span key={label} className="funshoot-launch-preview-tag">{label}</span>
                                    )) : <span className="funshoot-launch-preview-muted">No saved target labels</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="funshoot-launch-actions">
                    <button type="button" className="funshoot-launch-secondary" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="funshoot-launch-primary"
                        onClick={handleLaunch}
                        disabled={isSubmitting || isLoading || availableShooters.length === 0 || selectedShooters.length === 0}
                    >
                        {isSubmitting ? 'Starting...' : 'Start Fun Shoot'}
                    </button>
                </div>
            </div>

            {showQrScanner && (
                <div className="scoresheet-qr-modal-overlay active" onClick={() => setShowQrScanner(false)}>
                    <div className="scoresheet-qr-scanner-modal" onClick={(scanEvent) => scanEvent.stopPropagation()}>
                        <div className="scoresheet-qr-scanner-header">
                            <h5>Scan Stand QR Code</h5>
                            <button className="scoresheet-close-btn" onClick={() => setShowQrScanner(false)}>x</button>
                        </div>
                        {scanError && <div className="scoresheet-error-message">{scanError}</div>}
                        <div className="scoresheet-qr-scanner-container">
                            <video ref={ref} className="scoresheet-qr-video" autoPlay playsInline muted />
                        </div>
                        <div className="scoresheet-qr-scanner-footer">
                            <button className="scoresheet-outline-btn" onClick={() => setShowQrScanner(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FunShootQuickLaunch;
